import useSWR, { mutate } from 'swr';

import { fetcher } from 'src/utils/axios';

import { REFRESH_DELAY } from 'src/utils';


// ----------------------------------------------------------------------
// SWR Middlewares
// ----------------------------------------------------------------------


const smoothFetchMiddleware = (useSWRNext) => {

    /* Delayed fetcher to ensure minimum request time for better UX.
    * This helps to avoid rapid loading states that can be jarring to users.
    */
    const delayedFetcher = async (url) => {
        const startTime = Date.now();
        const result = await fetcher(url);
        const elapsed = Date.now() - startTime;
        const remainingDelay = Math.max(0, REFRESH_DELAY - elapsed);

        if (remainingDelay > 0) {
            await new Promise(resolve => setTimeout(resolve, remainingDelay));
        }

        return result;
    };


    return (key, defaultFetcher, config) => {
        const fetcher = config?.useDelayedFetcher ? delayedFetcher : defaultFetcher;
        return useSWRNext(key, fetcher, config);
    };
}

// ----------------------------------------------------------------------

const swrOptions = {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    use: [smoothFetchMiddleware],
};

// ----------------------------------------------------------------------


export function useFetch(url, args = {}) {
    const { params = {}, options = {} } = args;

    const filteredParams = Object.keys(params).reduce((acc, key) => {
        if (
            params[key] !== null &&
            params[key] !== undefined &&
            params[key] !== '' &&
            params[key] !== 'undefined' &&
            params[key] !== 'null' &&
            key !== 'orderBy' &&
            key !== 'order'
        ) {
            acc[key] = params[key];
        }
        return acc;
    }, {});

    if (params.orderBy) {
        // Django orders descending with a '-' prefix, so 'desc' is the prefixed
        // form. This was inverted, making every sorted list run opposite to the
        // direction the column header showed.
        filteredParams.ordering = `${params.order === 'desc' ? '-' : ''}${params.orderBy}`;
    }

    if (params.page !== undefined && params.page !== null) {
        filteredParams.page = (Number.parseInt(params.page) + 1) || 1;
    }

    // const searchParams = new URLSearchParams(filteredParams).toString();
    const searchParams = Object.keys(filteredParams)
        .map(key => {

            if (Array.isArray(filteredParams[key])) {
                return `${encodeURIComponent(key)}=${filteredParams[key].map(v => encodeURIComponent(v)).join(',')}`;
            }

            return `${encodeURIComponent(key)}=${encodeURIComponent(filteredParams[key])}`;
        })
        .join('&');

    const urlWithParams = (url && searchParams) ? `${url}?${searchParams}` : url;

    const { mutate, data, isLoading, error, isValidating: _isValidating } = useSWR(urlWithParams, fetcher, {
        ...swrOptions,
        ...options,
    });

    const isValidating = _isValidating || data === undefined;

    return {
        data,
        isLoading,
        error,
        isValidating,
        refresh: mutate,
    }
}

export function refresh(url) {
    return mutate(url, null, {
        revalidate: true,
    });
}


/**
 * Revalidate every cached request whose URL starts with `urlPrefix`.
 *
 * `refresh(url)` only touches one exact key, but list keys carry their query
 * string (page, ordering, filters), so refreshing the bare collection URL after
 * a mutation leaves the list the user actually sees untouched — and with
 * `revalidateIfStale: false` it stays stale indefinitely.
 */
export function invalidate(urlPrefix) {
    return mutate(
        (key) => typeof key === 'string' && key.startsWith(urlPrefix),
        undefined,
        { revalidate: true }
    );
}
